import { apiService } from './api';
import {
  SearchQuery,
  SearchResult,
  SearchSuggestionsResponse,
  SearchEntityType
} from '../types/search';
import { ENTITY_TONE, entityKindOf } from '../components/ui/colors';

class SearchService {
  private readonly baseUrl = '/api/search';

  async search(query: SearchQuery): Promise<SearchResult> {
    const params = new URLSearchParams();

    if (query.q) params.append('q', query.q);
    if (query.entityTypes?.length) {
      query.entityTypes.forEach(type => params.append('entityTypes', type));
    }
    if (query.organizationType) params.append('organizationType', query.organizationType);
    if (query.location) params.append('location', query.location);
    if (query.role) params.append('role', query.role);
    if (query.department) params.append('department', query.department);
    if (query.organizationId) params.append('organizationId', query.organizationId);
    if (query.stakeholderId) params.append('stakeholderId', query.stakeholderId);
    if (query.page) params.append('page', query.page.toString());
    if (query.limit) params.append('limit', query.limit.toString());
    if (query.sortBy) params.append('sortBy', query.sortBy);
    if (query.sortOrder) params.append('sortOrder', query.sortOrder);
    if (query.includeHighlights !== undefined) {
      params.append('includeHighlights', query.includeHighlights.toString());
    }

    const response = await apiService.get<SearchResult>(`${this.baseUrl}?${params.toString()}`);
    return response.data;
  }

  async getSuggestions(query: string): Promise<SearchSuggestionsResponse> {
    const response = await apiService.get<SearchSuggestionsResponse>(
      `${this.baseUrl}/suggestions?q=${encodeURIComponent(query)}`
    );
    return response.data;
  }

  getEntityTypeDisplayName(entityType: SearchEntityType): string {
    switch (entityType) {
      case SearchEntityType.STAKEHOLDER: return 'Stakeholder';
      case SearchEntityType.ORGANIZATION: return 'Organization';
      case SearchEntityType.ASSIGNMENT: return 'Assignment';
      case SearchEntityType.WIKI_PAGE: return 'Wiki Page';
      case SearchEntityType.RELATIONSHIP: return 'Relationship';
      case SearchEntityType.MEDIA_ASSET: return 'Media';
      default: return entityType;
    }
  }

  getEntityTypePluralName(entityType: SearchEntityType): string {
    switch (entityType) {
      case SearchEntityType.STAKEHOLDER: return 'Stakeholders';
      case SearchEntityType.ORGANIZATION: return 'Organizations';
      case SearchEntityType.ASSIGNMENT: return 'Assignments';
      case SearchEntityType.WIKI_PAGE: return 'Wiki Pages';
      case SearchEntityType.RELATIONSHIP: return 'Relationships';
      case SearchEntityType.MEDIA_ASSET: return 'Media Files';
      default: return entityType;
    }
  }

  /** Soft tint (background + text classes) of the entity hue for a result type */
  getEntityTypeColor(entityType: SearchEntityType): string {
    const kind = entityKindOf(entityType);
    return kind ? ENTITY_TONE[kind].soft : 'bg-neutral-100 text-neutral-600';
  }

  formatResultTitle(item: any, entityType: SearchEntityType): string {
    switch (entityType) {
      case SearchEntityType.STAKEHOLDER:
        return `${item.firstName} ${item.lastName}`;
      case SearchEntityType.ORGANIZATION:
        return item.name;
      case SearchEntityType.ASSIGNMENT: {
        const name = item.stakeholder ? `${item.stakeholder.firstName} ${item.stakeholder.lastName}` : '';
        const org = item.organization?.name || '';
        return name && org ? `${name} at ${org}` : name || org || item.role || 'Assignment';
      }
      case SearchEntityType.WIKI_PAGE:
        return item.title;
      case SearchEntityType.RELATIONSHIP: {
        const src = item.sourceName || 'Unknown';
        const tgt = item.targetName || 'Unknown';
        const label = item.label || item.category || 'Related';
        return `${src} \u2192 ${label} \u2192 ${tgt}`;
      }
      case SearchEntityType.MEDIA_ASSET:
        return item.originalName || item.fileName || 'Media File';
      default:
        return 'Unknown';
    }
  }

  formatResultSubtitle(item: any, entityType: SearchEntityType): string {
    switch (entityType) {
      case SearchEntityType.STAKEHOLDER:
        try {
          const assignments = Array.isArray(item.assignments) ? item.assignments : [];
          const current = assignments.find((a: any) => a.isCurrent) || assignments[0];
          if (current) {
            const orgName = current.organization?.name || current.organizationName || '';
            const role = current.role || '';
            if (role && orgName) return `${role} at ${orgName}`;
            if (role) return role;
            if (orgName) return orgName;
          }
          if (item.category) return item.category;
        } catch {}
        return item.location || 'Stakeholder';
      case SearchEntityType.ORGANIZATION: {
        const parts: string[] = [];
        if (item.type) parts.push(item.type);
        if (item.address) parts.push(item.address);
        return parts.join(' \u00B7 ') || 'Organization';
      }
      case SearchEntityType.ASSIGNMENT: {
        const aParts: string[] = [];
        if (item.role) aParts.push(item.role);
        if (item.department) aParts.push(item.department);
        if (item.isCurrent) aParts.push('Current');
        return aParts.join(' \u00B7 ') || 'Assignment';
      }
      case SearchEntityType.WIKI_PAGE: {
        const owner = item.stakeholder
          ? `${item.stakeholder.firstName} ${item.stakeholder.lastName}`
          : item.organization?.name;
        const contentSnippet = item.content
          ? item.content.replace(/<[^>]*>/g, '').substring(0, 120) + (item.content.length > 120 ? '...' : '')
          : '';
        if (owner && contentSnippet) return `${owner} \u00B7 ${contentSnippet}`;
        return owner || contentSnippet || 'Wiki Page';
      }
      case SearchEntityType.RELATIONSHIP: {
        const rParts: string[] = [];
        const typeLabel = item.type === 'ss' ? 'Person\u2013Person' : item.type === 'oo' ? 'Org\u2013Org' : 'Person\u2013Org';
        rParts.push(typeLabel);
        if (item.category) rParts.push(item.category);
        if (item.description) rParts.push(item.description.substring(0, 100));
        return rParts.join(' \u00B7 ');
      }
      case SearchEntityType.MEDIA_ASSET: {
        const mParts: string[] = [];
        if (item.type) mParts.push(item.type);
        if (item.mimeType) mParts.push(item.mimeType);
        if (item.fileSize) {
          const sizeMB = (item.fileSize / (1024 * 1024)).toFixed(1);
          mParts.push(`${sizeMB} MB`);
        }
        const ownerName = item.stakeholder
          ? `${item.stakeholder.firstName} ${item.stakeholder.lastName}`
          : item.organization?.name;
        if (ownerName) mParts.push(ownerName);
        return mParts.join(' \u00B7 ') || 'Media File';
      }
      default:
        return '';
    }
  }

  getResultUrl(item: any, entityType: SearchEntityType): string {
    switch (entityType) {
      case SearchEntityType.STAKEHOLDER: return `/stakeholders/${item.id}`;
      case SearchEntityType.ORGANIZATION: return `/organizations/${item.id}`;
      case SearchEntityType.ASSIGNMENT: return `/stakeholders/${item.stakeholderId || item.stakeholder?.id}`;
      case SearchEntityType.WIKI_PAGE: return `/wiki/${item.id}`;
      case SearchEntityType.RELATIONSHIP: return `/relationships`;
      case SearchEntityType.MEDIA_ASSET: {
        if (item.stakeholderId) return `/stakeholders/${item.stakeholderId}`;
        if (item.organizationId) return `/organizations/${item.organizationId}`;
        return `/media`;
      }
      default: return '#';
    }
  }

  getResultBreadcrumb(item: any, entityType: SearchEntityType): string {
    switch (entityType) {
      case SearchEntityType.STAKEHOLDER:
        return `staketory.com \u203A stakeholders \u203A ${(item.firstName || '').toLowerCase()}-${(item.lastName || '').toLowerCase()}`;
      case SearchEntityType.ORGANIZATION:
        return `staketory.com \u203A organizations \u203A ${(item.name || '').toLowerCase().replace(/\s+/g, '-')}`;
      case SearchEntityType.ASSIGNMENT: {
        const sName = item.stakeholder
          ? `${item.stakeholder.firstName}-${item.stakeholder.lastName}`.toLowerCase()
          : 'profile';
        return `staketory.com \u203A stakeholders \u203A ${sName} \u203A assignments`;
      }
      case SearchEntityType.WIKI_PAGE:
        return `staketory.com \u203A wiki \u203A ${(item.title || '').toLowerCase().replace(/\s+/g, '-')}`;
      case SearchEntityType.RELATIONSHIP:
        return `staketory.com \u203A relationships`;
      case SearchEntityType.MEDIA_ASSET:
        return `staketory.com \u203A media \u203A ${(item.originalName || '').toLowerCase()}`;
      default:
        return 'staketory.com';
    }
  }
}

export const searchService = new SearchService();
